import React, { useState, useEffect, useCallback } from 'react';
import { getSellers, createSeller, updateSeller, getBusinesses, getMe } from '../services/api';

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('es-MX')}`;
}

function isSuperAdmin(user) {
  return user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
}

function NewSellerModal({ superAdmin, businesses, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', password: '', businessId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name || !form.phone || !form.password) {
      setError('Todos los campos son obligatorios.');
      return;
    }
    if (superAdmin && !form.businessId) {
      setError('Selecciona el negocio.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!superAdmin) delete payload.businessId;
      await createSeller(payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear vendedor.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nuevo Vendedor</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}
          {superAdmin && (
            <div className="modal-form-group">
              <label>Negocio</label>
              <select className="status-select" value={form.businessId} onChange={set('businessId')} disabled={saving}>
                <option value="">Selecciona un negocio...</option>
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                ))}
              </select>
            </div>
          )}
          <div className="modal-form-group">
            <label>Nombre</label>
            <input className="text-input" value={form.name} onChange={set('name')} disabled={saving} />
          </div>
          <div className="modal-form-group">
            <label>Teléfono <span style={{ color: '#94a3b8', fontWeight: 400 }}>(con esto inicia sesión)</span></label>
            <input className="text-input" value={form.phone} onChange={set('phone')} disabled={saving} />
          </div>
          <div className="modal-form-group">
            <label>Contraseña</label>
            <input className="text-input" type="text" value={form.password} onChange={set('password')} disabled={saving}
              placeholder="El vendedor la usará para entrar a la app" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Crear'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Sellers({ user }) {
  const superAdmin = isSuperAdmin(user);
  const [sellers, setSellers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [myCode, setMyCode] = useState(user?.businessCode || null);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchSellers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSellers(search);
      setSellers(data.sellers || []);
    } catch (err) {
      setError('Error al cargar vendedores.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchSellers(); }, [fetchSellers]);

  // Load businesses for the super admin (to assign new sellers); load own code otherwise
  useEffect(() => {
    if (superAdmin) {
      getBusinesses().then((d) => setBusinesses(d.businesses || [])).catch(() => {});
    } else if (!myCode) {
      getMe().then((d) => setMyCode(d.businessCode)).catch(() => {});
    }
  }, [superAdmin, myCode]);

  const toggleActive = async (seller) => {
    setBusyId(seller.id);
    try { await updateSeller(seller.id, { active: !seller.active }); await fetchSellers(); }
    catch { setError('No se pudo actualizar el vendedor.'); }
    finally { setBusyId(null); }
  };

  const copyCode = () => {
    if (!myCode) return;
    navigator.clipboard?.writeText(myCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      {!superAdmin && myCode && (
        <div className="code-banner">
          Tu código de negocio: <span className="code-chip" onClick={copyCode} title="Clic para copiar">{myCode} {copied ? '✓' : '⧉'}</span>
          <span className="code-banner-hint">— compártelo con tus vendedores para que se registren en la app.</span>
        </div>
      )}

      <div className="filter-bar">
        <form className="search-form" onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }}>
          <input
            className="search-input"
            type="text"
            placeholder="Buscar por nombre, correo, teléfono..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {search ? (
            <button type="button" className="refresh-button" onClick={() => { setQ(''); setSearch(''); }}>Limpiar</button>
          ) : (
            <button type="submit" className="refresh-button">Buscar</button>
          )}
        </form>
        <button className="primary-button" onClick={() => setShowNew(true)}>+ Nuevo Vendedor</button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="table-container">
        {loading && sellers.length === 0 ? (
          <div className="loading-state"><div className="spinner"></div><p>Cargando vendedores...</p></div>
        ) : sellers.length === 0 ? (
          <div className="empty-state"><p>No hay vendedores{search ? ` para "${search}"` : ''}.</p></div>
        ) : (
          <table className="portings-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Teléfono</th>
                {superAdmin && <th>Negocio</th>}
                <th style={{ textAlign: 'center' }}>Portaciones</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th>Alta</th>
                <th style={{ textAlign: 'center' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map((s) => (
                <tr key={s.id} className={!s.active ? 'row-inactive' : ''}>
                  <td className="cell-id">#{s.id}</td>
                  <td>{s.name}</td>
                  <td className="cell-phone">{s.phone}</td>
                  {superAdmin && (
                    <td>{s.businessName || '-'}{s.businessCode ? <span className="admin-email"> ({s.businessCode})</span> : null}</td>
                  )}
                  <td style={{ textAlign: 'center' }}>{s.portingsCount}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`status-badge ${s.active ? 'badge-green' : 'badge-red'}`}>
                      {s.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="cell-date">{formatDate(s.createdAt)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className={s.active ? 'small-btn danger' : 'small-btn success'}
                      disabled={busyId === s.id}
                      onClick={() => toggleActive(s)}
                    >
                      {busyId === s.id ? '...' : s.active ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewSellerModal
          superAdmin={superAdmin}
          businesses={businesses}
          onClose={() => setShowNew(false)}
          onSaved={async () => { setShowNew(false); await fetchSellers(); }}
        />
      )}
    </>
  );
}

export default Sellers;
